import * as z from "zod";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import InputField from "widgets/shared/inputs/inputField";
import { useTranslation } from "react-i18next";
import LabeledField from "providers/labeledField";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "providers/form";
import GenericButton from "widgets/controls/buttons/generic";
import { useState } from "react";

// Schema definition for the form
const HvacUploadSchema = z.object({
  lines: z
    .array(
      z.object({
        address: z.string(),
        name: z.string(),
        SUM: z.string(),
      })
    )
    .optional(),
});

export type HvacUploadFormType = z.infer<typeof HvacUploadSchema>;

// Props for the form
type HvacUploadFormProps = {
  submit: (data: HvacUploadFormType) => void;
  clearData: () => void;
  onClose: () => void;
  defaultValues?: HvacUploadFormType;
  values?: HvacUploadFormType;
  loading?: boolean;
  disabled?: boolean;
};

const HvacUploadForm = ({
  submit,
  clearData,
  onClose,
  defaultValues,
  values,
  loading,
  disabled,
}: HvacUploadFormProps) => {
  const { t } = useTranslation();
  const [keys, setKeys] = useState(
    values?.lines.length > 0 ? Object.keys(values.lines[0]) : []
  );

  const form = useForm<HvacUploadFormType>({
    resolver: zodResolver(HvacUploadSchema),
    defaultValues,
    values,
  });

  const {
    control,
    watch,
    formState,
    setValue,
    getValues,
    handleSubmit,
    reset,
  } = form;
  const { errors } = formState;

  const onSubmit = async (formData: HvacUploadFormType) => {
    try {
      await submit(formData);
      reset(formData);
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const { fields, append, insert, remove } = useFieldArray({
    control,
    name: "lines", // Handle array for steps
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-2">
      <div className="flex w-full flex-col -mt-12">
          <div className="flex justify-end space-x-2">
            <GenericButton
              text={t("button.action.clear")}
              loading={loading || false}
              type="button"
              size="md"
              variant="outline"
              onClick={() => {
                clearData();
              }}
              color="secondary"
            />
            <GenericButton
              text={t("button.action.upload")}
              loading={loading || false}
              type="submit"
              size="md"
            />
          </div>
        </div>
        <div className={`mb-2 grid grid-cols-9 gap-2`}>
          <LabeledField
            key={"header1"}
            className="text-txt-xs"
            label={"ADDRESS"}
            id={"header1"}
            children={""}
            textColor="secondary"
          />
          <LabeledField
            key={"header2"}
            className="text-txt-xs"
            label={"NAME"}
            id={"header2"}
            children={""}
            textColor="secondary"
          />
          <LabeledField
            key={"header3"}
            className="text-txt-xs"
            label={"SUM"}
            id={"header3"}
            children={""}
            textColor="secondary"
          />
        </div>

        {fields.map((item, index) => (
          <div key={item.name} className={`grid grid-cols-9 gap-2`}>
            {/* snr */}
            <FormField
              control={control}
              name={`lines.${index}.address`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputField
                      sizes="sm"
                      placeholder={""}
                      id={`snr-${index}`}
                      {...field}
                      color="secondary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* meternumber */}
            <FormField
              control={control}
              name={`lines.${index}.name`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputField
                      sizes="sm"
                      placeholder={""}
                      id={`meternumber-${index}`}
                      {...field}
                      color="secondary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* model */}
            <FormField
              control={control}
              name={`lines.${index}.SUM`}
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputField
                      sizes="sm"
                      placeholder={""}
                      id={`model-${index}`}
                      {...field}
                      color="secondary"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ))}

        
      </form>
    </Form>
  );
};

export default HvacUploadForm;
